/**
 * TenantProvider — URL-derived tenant context.
 *
 * Per KEN_LAYER_7 §8.1.1: the shell NEVER reads tenant_id from a global
 * store on every render. The active tenant is always derived from the
 * URL's {tenant_slug} segment. This prevents cross-tab leaks.
 */

import { useRouterState } from "@tanstack/react-router";
import { createContext, useContext, useMemo, type ReactElement, type ReactNode } from "react";

export interface TenantContextValue {
  tenantSlug: string | undefined;
}

const Ctx = createContext<TenantContextValue>({ tenantSlug: undefined });

export function useTenantContext(): TenantContextValue {
  return useContext(Ctx);
}

export function TenantProvider({ children }: { children: ReactNode }): ReactElement {
  const matches = useRouterState({ select: (s) => s.matches });

  const tenantSlug = useMemo(() => {
    for (const m of matches) {
      const params = m.params as Record<string, string | undefined> | undefined;
      const slug = params?.tenantSlug;
      if (typeof slug === "string" && slug.length > 0) return slug;
    }
    return undefined;
  }, [matches]);

  const value = useMemo<TenantContextValue>(() => ({ tenantSlug }), [tenantSlug]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
